import React, { useState, useCallback, useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import BtnConfirm from 'ui/components/atoms/BtnConfirm';
import BreadCrumbs, { IBreadcrumbs } from 'ui/components/molecules/Breadcrumbs';
import BranchListSection from 'ui/components/organism/BranchListSection';
import PromoEventSection from 'ui/components/organism/PromoEventSection';
import CurrentInformationSection from 'ui/components/organism/CurrentInformationSection';
import FormFaqSection from 'ui/components/organism/FormFaqSection';
import ReviewSection from 'ui/components/organism/ReviewSection';
import eventTradeInterest from 'ui/lib/gtm/eventTradeInterest';
import ArrowForward from 'ui/components/atoms/Icons/ArrowForward';
import useElementVisibility from '../../../hooks/useElementVisibility';
import BranchInfoSection from 'ui/components/organism/BranchInfoSection';
import useIsNativeView from 'ui/hooks/useIsNativeView';
import { IRootState } from '../../../store';
import { IFaq } from '../../molecules/FaqAccordion';
import { Article, Province } from '../../../interface';
import useImpressionBranchSales from '../../../hooks/emarsys/branch-sales/useImpressionBranchSales';
import { FEATURE, PAGE_NAME_BRANCH_SALES } from '../../../constants/emarsys/branch-sales';

const ModalBranchFilter = dynamic(() => import('ui/components/molecules/ModalBranchFilter'), {
  ssr: false,
});

const TanyaTasia = dynamic(() => import('ui/components/atoms/TanyaTasia'), {
  ssr: false,
});

const InquiryFormSectionBranch = dynamic(
  () => import('ui/components/organism/InquiryFormSectionBranch'),
  {
    ssr: false,
  }
);

const InquiryFormMobileSectionBranch = dynamic(
  () => import('ui/components/organism/InquiryFormMobileSectionBranch'),
  {
    ssr: false,
  }
);

const InquiryFormModalSectionBranch = dynamic(
  () => import('ui/components/organism/InquiryFormModalSectionBranch'),
  {
    ssr: false,
  }
);

type IBranchCity = {
  isocode?: string;
  cityInformationHeader?: string;
  cityInformationDescription?: string;
  promoInformationHeader?: string;
  promoInformationDescription?: string;
  eventInformationHeader?: string;
  eventInformationDescription?: string;
  dealerInformationHeader?: string;
  dealerInformationDescription?: string;
  informasiFaqTitle?: string;
  informasiCustomerReviewTitle?: string;
  seoTitleTag?: string;
  seoRobotTag?: string;
  seoAltImage?: string;
  seoKeyword?: string;
  seoMetaDescription?: string;
};

type IBranch = {
  city?: IBranchCity;
};

interface IBranchListingPageTemplateProps {
  breadcrumbsItems?: IBreadcrumbs[];
  branchList?: IBranch[];
  branchListInquiry?: IBranch[];
  city?: string;
  cityCode?: string;
  citySlug?: string;
  faqTitleList?: string;
  promoList?: Article[];
  newsList?: Article[];
  faqs?: IFaq[];
  reviews?: { rating?: number }[];
  branchFilter?: {
    sorted?: boolean;
  };
  hideAllPromotionButton?: boolean;
  hideSeeAllNewsButton?: boolean;
  customerReviewTitle?: string;
  branchProvince?: Province;
  isProvinceReady?: boolean;
  feature?: string;
  branchByCity?: any[];
  sectionPage?: string;
  messageBranch?: string;
}

const BranchListingPageTemplate = ({
  breadcrumbsItems = [],
  branchList = [],
  branchListInquiry = [],
  city = '',
  cityCode = '',
  citySlug = '',
  faqTitleList = '',
  promoList = [],
  newsList = [],
  faqs,
  reviews = [],
  branchFilter = {},
  hideAllPromotionButton = false,
  hideSeeAllNewsButton = false,
  customerReviewTitle = '',
  branchProvince,
  isProvinceReady = true,
  feature,
  sectionPage = '',
  messageBranch,
}: IBranchListingPageTemplateProps) => {
  const workshopPromoImpressionRef = useRef(null);
  const workshopUpdateImpressionRef = useRef(null);
  const nearestWorkshopImpressionRef = useRef(null);
  const staticInquiryFormRef = useRef(null);
  const workshopFAQImpressionRef = useRef(null);
  const buttonInquiryListRef = useRef(null);
  const initialButtonPosition = useRef(0);
  const { isGTMReady } = useSelector((state: any) => state.dataGTM || {});

  const [showQuickInquiry, setShowQuickInquiry] = useState(false);
  const [showBranchFilter, setShowBranchFilter] = useState(false);
  const cityDetail = branchList?.[0]?.city ?? {};
  const { isMobileScreen, isLargeScreen } = useSelector((state: IRootState) => state.page);
  const [isButtonInquiryVisible, setIsButtonInquiryVisible] = useState(false);
  const isButtonStuck = useElementVisibility(buttonInquiryListRef);
  const isInquiryFormInView = useElementVisibility(staticInquiryFormRef);

  useEffect(() => {
    const handleScroll = () => {
      const currentButtonPosition = buttonInquiryListRef.current?.getBoundingClientRect().top;
      const scrollDifference = initialButtonPosition.current - currentButtonPosition;

      // Jika perubahan posisi lebih dari 100px, atur setIsButtonInquiryVisible menjadi false
      if (scrollDifference > 25 && currentButtonPosition <= initialButtonPosition.current) {
        setIsButtonInquiryVisible(true);
      } else {
        setIsButtonInquiryVisible(false);
      }
    };

    // Simpan posisi awal elemen saat komponen dimount
    initialButtonPosition.current = buttonInquiryListRef.current?.getBoundingClientRect().top;

    // Tambahkan event listener untuk mendengarkan perubahan scroll
    window.addEventListener('scroll', handleScroll);

    // Membersihkan event listener saat komponen unmount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isNativeView = useIsNativeView();

  const handleToggleFilter = useCallback(() => {
    setShowBranchFilter((prevValue) => !prevValue);
  }, []);

  const handleClickInquiry = () => {
    setShowQuickInquiry(true);
    eventTradeInterest();
  };

  // For Impression Workshop Promo
  useImpressionBranchSales({
    firstRef: workshopPromoImpressionRef,
    pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
    pageTitle: cityDetail?.cityInformationHeader,
    breadcrumbItems: breadcrumbsItems,
    sectionName: 'promo bengkel',
    isProvinceReady,
    newParams: {
      province: branchProvince?.name ?? '',
      province_code: branchProvince?.isocode ?? '',
      city: city,
      city_code: cityCode,
      ...(breadcrumbsItems?.[2]?.name
        ? {
            menu_cat_lvl2: breadcrumbsItems?.[2]?.name?.toUpperCase(),
          }
        : {}),
    },
  });

  // For Impression Workshop Update
  useImpressionBranchSales({
    firstRef: workshopUpdateImpressionRef,
    pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
    pageTitle: cityDetail?.cityInformationHeader,
    breadcrumbItems: breadcrumbsItems,
    sectionName: 'update bengkel',
    isProvinceReady,
    newParams: {
      province: branchProvince?.name ?? '',
      province_code: branchProvince?.isocode ?? '',
      city: city,
      city_code: cityCode,
      ...(breadcrumbsItems?.[2]?.name
        ? {
            menu_cat_lvl2: breadcrumbsItems?.[2]?.name?.toUpperCase(),
          }
        : {}),
    },
  });

  // For Impression Nearest Workshop
  useImpressionBranchSales({
    firstRef: nearestWorkshopImpressionRef,
    pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
    pageTitle: cityDetail?.cityInformationHeader,
    breadcrumbItems: breadcrumbsItems,
    sectionName: 'bengkel dan dealer toyota terdekat',
    isProvinceReady,
    newParams: {
      province: branchProvince?.name ?? '',
      province_code: branchProvince?.isocode ?? '',
      city: city,
      city_code: cityCode,
      ...(breadcrumbsItems?.[2]?.name
        ? {
            menu_cat_lvl2: breadcrumbsItems?.[2]?.name?.toUpperCase(),
          }
        : {}),
    },
  });

  // For Impression Nearest A Quote
  useImpressionBranchSales({
    firstRef: staticInquiryFormRef,
    pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
    pageTitle: cityDetail?.cityInformationHeader,
    breadcrumbItems: breadcrumbsItems,
    sectionName: 'minta penawaran',
    isProvinceReady,
    newParams: {
      province: branchProvince?.name ?? '',
      province_code: branchProvince?.isocode ?? '',
      city: city,
      city_code: cityCode,
      ...(breadcrumbsItems?.[2]?.name
        ? {
            menu_cat_lvl2: breadcrumbsItems?.[2]?.name?.toUpperCase(),
          }
        : {}),
    },
  });

  // For Impression Nearest A Quote
  useImpressionBranchSales({
    firstRef: workshopFAQImpressionRef,
    pageName: PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA,
    pageTitle: cityDetail?.cityInformationHeader,
    breadcrumbItems: breadcrumbsItems,
    sectionName: 'faq bengkel',
    isProvinceReady,
    newParams: {
      province: branchProvince?.name ?? '',
      province_code: branchProvince?.isocode ?? '',
      city: city,
      city_code: cityCode,
      ...(breadcrumbsItems?.[2]?.name
        ? {
            menu_cat_lvl2: breadcrumbsItems?.[2]?.name?.toUpperCase(),
          }
        : {}),
    },
  });

  return (
    <>
      <BreadCrumbs
        isMobileScreen={false}
        breadcrumbsClassName={'!hidden lg:!block'}
        items={breadcrumbsItems}
      />

      <BranchListSection
        data={branchList}
        onClickFilter={handleToggleFilter}
        handleClickInquiry={handleClickInquiry}
        city={city}
        cityCode={cityCode}
        sorted={branchFilter.sorted}
        title={cityDetail?.cityInformationHeader}
        description={cityDetail?.cityInformationDescription}
        branchProvince={branchProvince}
        breadcrumbItems={breadcrumbsItems}
        isProvinceReady={isProvinceReady}
        messageBranch={messageBranch}
      />

      {promoList?.length !== 0 && (
        <div ref={workshopPromoImpressionRef}>
          <PromoEventSection
            data={promoList}
            title={cityDetail.promoInformationHeader}
            description={cityDetail.promoInformationDescription}
            baseUrl={`${citySlug}/promosi`}
            hideAllPromotionButton={hideAllPromotionButton}
            pageTitle={cityDetail?.cityInformationHeader}
            breadcrumbItems={breadcrumbsItems}
            branchProvince={branchProvince}
            city={city}
            cityCode={cityCode}
            feature={FEATURE.BRANCH_LISTING_PAGE}
          />
        </div>
      )}

      {newsList?.length !== 0 && (
        <div ref={workshopUpdateImpressionRef}>
          <CurrentInformationSection
            data={newsList}
            title={cityDetail.eventInformationHeader}
            description={cityDetail.eventInformationDescription}
            baseUrl={`${citySlug}/berita-dan-tips`}
            hideSeeAllNewsButton={hideSeeAllNewsButton}
            pageTitle={cityDetail?.cityInformationHeader}
            breadcrumbItems={breadcrumbsItems}
            branchProvince={branchProvince}
            city={city}
            cityCode={cityCode}
            feature={feature}
          />
        </div>
      )}

      {cityDetail?.dealerInformationHeader && (
        <section className="py-[30px] border-t-4 lg:py-20" ref={nearestWorkshopImpressionRef}>
          <BranchInfoSection
            title={cityDetail.dealerInformationHeader}
            description={cityDetail.dealerInformationDescription}
            pageName={PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA}
            pageTitle={cityDetail?.cityInformationHeader}
            breadcrumbItems={breadcrumbsItems}
            branchProvince={branchProvince}
            city={city}
            sectionName="bengkel dan dealer toyota terdekat"
            buttonName="lihat selengkapnya"
            cityCode={cityCode}
            feature={feature}
          />
        </section>
      )}

      {/* Static branch inquiry section */}
      <div ref={staticInquiryFormRef}>
        <InquiryFormSectionBranch
          subTitle={
            <span>
              Dapatkan penawaran khusus produk{' '}
              <span className="font-semibold">Auto2000 Di {city}</span> untuk Anda
            </span>
          }
          showBranchDropdown={true}
          branchList={branchListInquiry}
          city={city}
          redirectUrl={`/${citySlug}/inquiry/success`}
          sourcePage="cityPage"
          pageName="dealerlisting page"
          pageNameEmarsys={PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA}
          pageTitle={cityDetail?.cityInformationHeader}
          breadcrumbItems={breadcrumbsItems}
          branchProvince={branchProvince}
          sectionName="minta penawaran"
          buttonName="minta penawaran"
          cityCode={cityCode}
          feature={feature}
        />
      </div>

      <div ref={workshopFAQImpressionRef}>
        <FormFaqSection faqs={faqs} faqTitle={faqTitleList} />
      </div>

      {reviews.length > 0 && <ReviewSection reviews={reviews} title={customerReviewTitle} />}

      <BreadCrumbs items={breadcrumbsItems} />

      {/* Tanya Tasia & Back to Top Button */}
      {isGTMReady &&
        (!isButtonInquiryVisible ? (
          <TanyaTasia
            backToTopClassName={`${!isLargeScreen ? '!bottom-[140px]' : '!bottom-[30px]'}`}
            isBranchListingPage={true}
          />
        ) : (
          <TanyaTasia
            backToTopClassName={`${!isLargeScreen ? '!bottom-[86px]' : '!bottom-[30px]'}`}
            isBranchListingPage={true}
          />
        ))}

      {/* CTA Button for mobile screen */}
      <div
        className={`sticky w-full z-20 mt-[10px] lg:hidden ${
          isInquiryFormInView ? 'hidden' : 'block'
        } ${isNativeView ? 'bottom-0' : 'bottom-[69px]'} ${isButtonStuck ? 'top-0' : ''}`}
        ref={buttonInquiryListRef}
      >
        <BtnConfirm
          id="btn-mobile-saya-tertarik"
          block={true}
          className="bg-supportiveRed text-white p-4 h-[55px]"
          size="large"
          iconType="fill"
          onClick={() => {
            setShowQuickInquiry(true);
            eventTradeInterest();
          }}
        >
          <div className="flex justify-end items-center gap-2">
            <p className="text-base font-bold uppercase">Minta Penawaran</p>
            <ArrowForward color="#F8F8F8" />
          </div>
        </BtnConfirm>
      </div>

      {/* Branch inquiry form mobile modal */}
      {isMobileScreen && (
        <InquiryFormMobileSectionBranch
          formOpen={showQuickInquiry}
          showBranchDropdown={true}
          branchList={branchListInquiry}
          redirectUrl={`/${citySlug}/inquiry/success`}
          sourcePage="cityPage"
          onClose={() => setShowQuickInquiry(false)}
          pageName="dealerlisting page"
          pageNameEmarsys={PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA}
          pageTitle={cityDetail?.cityInformationHeader}
          breadcrumbItems={breadcrumbsItems}
          branchProvince={branchProvince}
          city={city}
          sectionName="minta penawaran"
          buttonName="minta penawaran"
          cityCode={cityCode}
          feature={feature}
        />
      )}

      {/* Branch inquiry form desktop modal */}
      {!isMobileScreen && (
        <InquiryFormModalSectionBranch
          title={'Minta Penawaran'}
          subTitle=""
          isFormStack={true}
          showQuickInquiry={showQuickInquiry}
          showBranchDropdown={true}
          branchList={branchListInquiry}
          redirectUrl={`/${citySlug}/inquiry/success`}
          sourcePage="cityPage"
          setShowQuickInquiry={(bool) => setShowQuickInquiry(bool)}
          pageName="dealerlisting page"
          pageNameEmarsys={PAGE_NAME_BRANCH_SALES.LIST_DEALER_TOYOTA}
          pageTitle={cityDetail?.cityInformationHeader}
          breadcrumbItems={breadcrumbsItems}
          branchProvince={branchProvince}
          city={city}
          sectionName="minta penawaran"
          buttonName="minta penawaran"
          cityCode={cityCode}
          feature={feature}
        />
      )}

      <ModalBranchFilter
        visible={showBranchFilter}
        branchProvince={branchProvince}
        breadcrumbItems={breadcrumbsItems}
        pageTitle={cityDetail?.cityInformationHeader}
        city={city}
        cityCode={cityCode}
        onClose={handleToggleFilter}
      />
    </>
  );
};
export default BranchListingPageTemplate;
